import React, { useState } from 'react';

const FAQ_ITEMS = [
  {
    q: "How much does a typical project cost?",
    a: "Most engagements fall between $1,500 and $25,000, depending on scope. We publish starting prices for each service category and always quote a fixed price before work begins — no hourly surprises."
  },
  {
    q: "How long does a project take?",
    a: "A focused dashboard or automation build usually takes 4-6 weeks from kickoff to handoff. A larger data platform may run 8-12 weeks. A Data & Automation Audit is completed in 1-2 weeks."
  },
  {
    q: "Do you work remotely?",
    a: "Yes — we're remote-first. We work with clients across the United States using async-friendly processes, scheduled syncs, and collaborative tools. Travel is available for longer engagements when it adds value."
  },
  {
    q: "What technologies do you build with?",
    a: "We match the tool to the problem. Common picks include Power BI, Tableau, or custom React dashboards for reporting; Python, Node.js, and .NET for custom applications; Snowflake, BigQuery, and Postgres for data warehouses; and Azure OpenAI, Anthropic, and OpenAI APIs for AI features."
  },
  {
    q: "Will our team be able to maintain what you build?",
    a: "Yes, and that's a deliberate focus. Every project includes documentation, handoff sessions, and training so your internal team owns the tool after launch. We don't build black boxes that require ongoing consulting to keep alive."
  },
  {
    q: "Can you sign an NDA or custom contract?",
    a: "Yes. We're happy to sign mutual NDAs before any detailed conversation and work under your MSA, SOW, or standard contract language. Data confidentiality is built into our process from the first call."
  },
  {
    q: "How do we get started?",
    a: "The easiest path is a free 30-minute call to talk through your situation. From there, if the fit is right, we recommend starting with a Data & Automation Audit — a low-cost engagement that produces a concrete roadmap before any larger commitment."
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="bg-white py-20 lg:py-28">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16 mb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            The things prospects ask us most often, answered straight.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="space-y-4">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-xl"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                >
                  <span className="text-lg md:text-xl font-semibold text-gray-900 pr-4">
                    {item.q}
                  </span>
                  <svg
                    className={`w-6 h-6 text-blue-600 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  id={`faq-panel-${i}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-6 pb-6 text-gray-600 text-lg leading-relaxed">
                    {item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            Still have questions?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="#contact"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-full transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-offset-2"
            >
              Send Us a Message
            </a>
            <a
              href="tel:+19286070710"
              className="inline-flex items-center border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold py-3 px-6 rounded-full transition-all duration-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call (928) 607-0710
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
